import { supabase } from '../client';
import type { Database } from '../generated';

export type SiteConfig = Database['core']['Tables']['site_config']['Row'];
export type SiteConfigUpdate = Database['core']['Tables']['site_config']['Update'];

/**
 * Reads the singleton PeptideClients company info. Public (anon-readable);
 * used by docs.peptideclients.com to populate legal-page tokens at request
 * time so updates flow without redeploying the docs site.
 */
export async function getSiteConfig(): Promise<SiteConfig | null> {
  const { data, error } = await supabase()
    .schema('core')
    .from('site_config')
    .select('*')
    .eq('id', 1)
    .maybeSingle();
  if (error) throw error;
  return data;
}

/**
 * Updates the singleton. Server-side RLS only allows this for users in
 * `core.platform_admins`. Any other caller will get a 401/empty result.
 */
export async function updateSiteConfig(patch: SiteConfigUpdate): Promise<SiteConfig> {
  const { data, error } = await supabase()
    .schema('core')
    .from('site_config')
    .update(patch)
    .eq('id', 1)
    .select('*')
    .single();
  if (error) throw error;
  return data as SiteConfig;
}

/**
 * Returns true if the current authenticated user is a platform admin.
 * Routes the call through the SQL function so we don't have to teach the
 * client about the `platform_admins` table directly.
 */
export async function isPlatformAdmin(): Promise<boolean> {
  const { data, error } = await supabase().schema('core').rpc('is_platform_admin');
  if (error) return false;
  return Boolean(data);
}

// -------------------------------------------------------------------------
// Public URLs surface
//
// Eight cross-host URLs landed in core.site_config as part of the
// 20260525140000 migration so the Admin app can re-point storefront
// CTAs (Sign in / Create account / docs / etc.) without a redeploy.
// The shape below is the strict, non-null contract every caller wants
// -- it falls back to today's production defaults when a column reads
// null (fresh-cluster case, or row id=1 predates the alter table).
// -------------------------------------------------------------------------

export interface PublicSiteUrls {
  /** Operator app origin -- https://app.peptideclients.com */
  app_url: string;
  /** Marketing site origin -- https://peptideclients.com */
  marketing_url: string;
  /** Buyer marketplace SPA origin -- https://shop.peptideclients.com */
  shop_app_url: string;
  /** Vendor portal SPA origin -- https://sell.peptideclients.com */
  sell_app_url: string;
  /** Docs / legal site origin -- https://docs.peptideclients.com */
  docs_url: string;
  /** Public landing the shop SPA bounces unauthed root visitors to. */
  shop_landing_url: string;
  /** Where every "Create account" / "Open an account" CTA navigates to. */
  signup_url: string;
  /**
   * Where every "Sign in" CTA navigates to. Defaults to the SHOP-hosted
   * login at `https://shop.peptideclients.com/sign-in` (the SignInPage
   * mounted in `apps/shop/src/auth/SignInPage.tsx`) so a buyer who
   * arrives on a vendor's public storefront and clicks "Sign in" stays
   * on the shop host instead of being bounced into the operator-app
   * (CRM) shell, where they would land on a settings page they don't
   * have access to and feel lost.
   */
  signin_url: string;
}

export const DEFAULT_PUBLIC_SITE_URLS: PublicSiteUrls = {
  app_url: 'https://app.peptideclients.com',
  marketing_url: 'https://peptideclients.com',
  shop_app_url: 'https://shop.peptideclients.com',
  sell_app_url: 'https://sell.peptideclients.com',
  docs_url: 'https://docs.peptideclients.com',
  shop_landing_url: 'https://peptideclients.com/shop.html',
  signup_url: 'https://app.peptideclients.com/sign-in?mode=signup',
  signin_url: 'https://shop.peptideclients.com/sign-in',
};

/**
 * Reads the 8 public-URL columns from core.site_config, falling back
 * to DEFAULT_PUBLIC_SITE_URLS for any null / missing field. Anon-safe:
 * `site_config_read_anyone` policy already exposes these columns to
 * the anon role, so the public storefront page can call this without
 * a session.
 *
 * Errors swallowed by design: a brand-new cluster or a transient RLS
 * misconfiguration shouldn't break the page; it should fall back to
 * the same hard-coded URLs the old code used.
 */
export async function getPublicSiteUrls(): Promise<PublicSiteUrls> {
  try {
    const { data, error } = await supabase()
      .schema('core')
      .from('site_config')
      .select(
        'app_url, marketing_url, shop_app_url, sell_app_url, docs_url, shop_landing_url, signup_url, signin_url',
      )
      .eq('id', 1)
      .maybeSingle();
    if (error || !data) return DEFAULT_PUBLIC_SITE_URLS;
    const row = data as Partial<SiteConfig>;
    return {
      app_url: row.app_url ?? DEFAULT_PUBLIC_SITE_URLS.app_url,
      marketing_url: row.marketing_url ?? DEFAULT_PUBLIC_SITE_URLS.marketing_url,
      shop_app_url: row.shop_app_url ?? DEFAULT_PUBLIC_SITE_URLS.shop_app_url,
      sell_app_url: row.sell_app_url ?? DEFAULT_PUBLIC_SITE_URLS.sell_app_url,
      docs_url: row.docs_url ?? DEFAULT_PUBLIC_SITE_URLS.docs_url,
      shop_landing_url:
        row.shop_landing_url ?? DEFAULT_PUBLIC_SITE_URLS.shop_landing_url,
      signup_url: row.signup_url ?? DEFAULT_PUBLIC_SITE_URLS.signup_url,
      signin_url: row.signin_url ?? DEFAULT_PUBLIC_SITE_URLS.signin_url,
    };
  } catch {
    return DEFAULT_PUBLIC_SITE_URLS;
  }
}

/**
 * Anon-keyed `fetch` variant for the PublicStorefrontApp -- it does
 * NOT call `initSupabase()` (see apps/shop/src/PublicStorefrontApp.tsx),
 * so the singleton client from `supabase()` isn't available. This
 * mirrors the pattern used by getPublicVendorCryptoAddressesViaFetch
 * for the same reason.
 */
export async function getPublicSiteUrlsViaFetch(
  supabaseUrl: string,
  anonKey: string,
): Promise<PublicSiteUrls> {
  try {
    const u = new URL(`${supabaseUrl}/rest/v1/site_config`);
    u.searchParams.set('id', 'eq.1');
    u.searchParams.set(
      'select',
      'app_url,marketing_url,shop_app_url,sell_app_url,docs_url,shop_landing_url,signup_url,signin_url',
    );
    const resp = await fetch(u.toString(), {
      headers: {
        apikey: anonKey,
        authorization: `Bearer ${anonKey}`,
        'accept-profile': 'core',
      },
    });
    if (!resp.ok) return DEFAULT_PUBLIC_SITE_URLS;
    const arr = (await resp.json()) as Array<Partial<SiteConfig>>;
    const row = arr[0];
    if (!row) return DEFAULT_PUBLIC_SITE_URLS;
    return {
      app_url: row.app_url ?? DEFAULT_PUBLIC_SITE_URLS.app_url,
      marketing_url: row.marketing_url ?? DEFAULT_PUBLIC_SITE_URLS.marketing_url,
      shop_app_url: row.shop_app_url ?? DEFAULT_PUBLIC_SITE_URLS.shop_app_url,
      sell_app_url: row.sell_app_url ?? DEFAULT_PUBLIC_SITE_URLS.sell_app_url,
      docs_url: row.docs_url ?? DEFAULT_PUBLIC_SITE_URLS.docs_url,
      shop_landing_url:
        row.shop_landing_url ?? DEFAULT_PUBLIC_SITE_URLS.shop_landing_url,
      signup_url: row.signup_url ?? DEFAULT_PUBLIC_SITE_URLS.signup_url,
      signin_url: row.signin_url ?? DEFAULT_PUBLIC_SITE_URLS.signin_url,
    };
  } catch {
    return DEFAULT_PUBLIC_SITE_URLS;
  }
}
